import {useEffect, useRef} from 'react';
import {useSearchParams} from 'react-router-dom';
import {showHttpErrorToast} from '@common/utils/http/show-http-error-toast';
import {toast} from '@common/ui/toast/toast';
import {message} from '@common/i18n/message';
import {reloadAccountUsage} from '@app/editor/use-account-usage';
import {useConfirmAiTopupCheckout} from '@app/editor/ai/usage/use-ai-usage-topups';

const SESSION_MARKER_KEY = 'aiTopupSessionConfirmed';

export function useAiTopupCheckoutReturn() {
  const [searchParams, setSearchParams] = useSearchParams();
  const confirmTopup = useConfirmAiTopupCheckout();
  const requestedSessionRef = useRef<string | null>(null);

  useEffect(() => {
    const status = searchParams.get('topupStatus');
    const sessionId = searchParams.get('topupSession');
    if (status !== 'success' || !sessionId) {
      return;
    }

    const alreadyConfirmed = sessionStorage.getItem(SESSION_MARKER_KEY);
    if (alreadyConfirmed === sessionId || requestedSessionRef.current === sessionId) {
      return;
    }

    requestedSessionRef.current = sessionId;
    confirmTopup.mutate(sessionId, {
      onSuccess: () => {
        sessionStorage.setItem(SESSION_MARKER_KEY, sessionId);
        reloadAccountUsage();
        toast(message('Extra AI usage has been added to your account.'));
        clearTopupParams(searchParams, setSearchParams);
      },
      onError: err => {
        showHttpErrorToast(err);
        clearTopupParams(searchParams, setSearchParams);
      },
    });
  }, [confirmTopup, searchParams, setSearchParams]);
}

function clearTopupParams(
  current: URLSearchParams,
  setSearchParams: any,
) {
  const next = new URLSearchParams(current);
  next.delete('topupStatus');
  next.delete('topupSession');
  setSearchParams(next, {replace: true});
}
